// El contrato del lienzo de un diseño — ADR-2026-09-17-lanzar-es-el-unico-camino. `lanzar`
// es el ÚNICO camino de generación: los botones de la barra, los paneles y las
// herramientas del agente pasan todos por la misma función, con los mismos setters
// que usa la pantalla (memoria "capacidades como tools, no opciones").
//
// La MEZCLA que se está armando vive acá, en memoria, y no en el diseño guardado
// (ADR-2026-09-17-la-mezcla-no-se-guarda): cerrarla la borra, y volver a mezclar es
// volver a tocar las imágenes.

import { createContext, useContext } from 'react';
import type { ChatAttachment } from '../../agentAttachments';
import { DISENO_VACIO, type AccionDeDiseno, type DisenoGuardado, type ResultadoDeMezcla } from './disenoGuardado';
import type { AccionConBoton } from './accionesDeDiseno';
import type { Marca } from './marcador';

export interface PanelAbierto {
  accion: AccionConBoton;
  celdaId: string;
}

export interface ParteDeMezcla {
  celdaId: string;
  /** Qué tomar de esa imagen, tal cual se escribe (se recorta recién al armar el prompt). */
  instruccion: string;
  /** Lo pintado con el Marcador: dónde está, en esa imagen, lo que se toma (ADR-2026-09-21-el-marcador-de-la-mezcla). */
  marca?: Marca;
}

export interface MezclaEnCurso {
  baseId: string;
  partes: ParteDeMezcla[];
  /** La instrucción general, opcional. */
  instruccion: string;
  cantidad: number;
  /** Qué tipo de imagen sale (ADR-2026-09-18-la-mezcla-elige-realista-o-como-la-base). */
  resultado: ResultadoDeMezcla;
  /** Lo pintado con el Marcador sobre la base: dónde va el cambio (ADR-2026-09-21-el-marcador-de-la-mezcla). */
  marcaBase?: Marca;
}

export type ResultadoDeLanzar = { ok: true; tandaId: string; total: number } | { ok: false; motivo: string };

export interface OpcionesDeLanzar {
  instruccion?: string;
  partes?: ParteDeMezcla[];
  instruccionGeneral?: string;
  /** Mezcla: qué tipo de imagen sale; sin él, el de la mezcla en curso o `realista`. */
  resultado?: ResultadoDeMezcla;
  /** Mezcla: lo pintado sobre la base (dónde va el cambio); sin ella, la de la mezcla en curso. */
  marcaBase?: Marca;
  textoBase?: string;
  /** Cuántas imágenes; sin ella, la cantidad por defecto de la acción. */
  cantidad?: number;
}

/** Una imagen de la biblioteca del diseño: las propias y las filas de sus tandas, estén o no en el lienzo. */
export interface ImagenDeBiblioteca {
  id: string;
  titulo: string;
  enLienzo: boolean;
}

export interface ValorDeDiseno {
  diseno: DisenoGuardado;
  companyId: string | null;
  /** URL para mostrar una celda: la miniatura firmada (propia u opt/ de la fila). */
  urlDe: (celdaId: string) => string | undefined;
  tituloDe: (celdaId: string) => string;
  /** El chip de chat de una imagen PROPIA (subida al diseño): lo usan "Agregar al chat" y
   *  la selección del lienzo. `null` si esa celda no es propia o no tiene miniatura. */
  adjuntoDePropia: (celdaId: string) => ChatAttachment | null;
  /** Todas las celdas con imagen (no pendientes) que están EN el lienzo, en su orden. */
  celdas: () => { id: string; titulo: string }[];
  /** La biblioteca del diseño: lo que hay, en el lienzo o quitado (ADR-2026-09-17-agregar-imagen-es-la-biblioteca-del-diseno). */
  biblioteca: () => ImagenDeBiblioteca[];
  lanzar: (accion: AccionDeDiseno, celdaId: string | null, opts?: OpcionesDeLanzar) => Promise<ResultadoDeLanzar>;
  panel: PanelAbierto | null;
  abrirPanel: (p: PanelAbierto | null) => void;
  mezcla: MezclaEnCurso | null;
  /** Arranca (con la prenda), cambia o cierra (`null`) la mezcla en curso. */
  setMezcla: (fn: (m: MezclaEnCurso | null) => MezclaEnCurso | null) => void;
  /** Suma o saca una parte de la mezcla en curso; abre su instrucción al sumarla. */
  alternarParte: (celdaId: string) => void;
  /** Genera la mezcla en curso por `lanzar` y la cierra si salió; avisa el error si no. */
  generarMezcla: () => void;
  parteEnEdicion: string | null;
  setParteEnEdicion: (id: string | null) => void;
  /** El Marcador de la barra (ADR-2026-09-21-el-marcador-de-la-mezcla): mientras está prendido, arrastrar sobre una imagen de la mezcla la pinta. */
  marcador: boolean;
  setMarcador: (v: boolean) => void;
  setDiseno: (fn: (d: DisenoGuardado) => DisenoGuardado) => void;
  quitarDelLienzo: (celdaId: string) => void;
  /** Vuelve a poner en el lienzo una imagen de la biblioteca que se había quitado. */
  devolverAlLienzo: (celdaId: string) => void;
  agregarDeCreaciones: (pick: { id: string; ratio: number; titulo: string }) => void;
  abrirAgregar: (archivos?: File[]) => void;
  avisarError: (msg: string) => void;
}

export const DisenoContext = createContext<ValorDeDiseno>({
  diseno: DISENO_VACIO,
  companyId: null,
  urlDe: () => undefined,
  tituloDe: () => '',
  adjuntoDePropia: () => null,
  celdas: () => [],
  biblioteca: () => [],
  lanzar: async () => ({ ok: false, motivo: 'Sin lienzo' }),
  panel: null,
  abrirPanel: () => {},
  mezcla: null,
  setMezcla: () => {},
  alternarParte: () => {},
  generarMezcla: () => {},
  parteEnEdicion: null,
  setParteEnEdicion: () => {},
  marcador: false,
  setMarcador: () => {},
  setDiseno: () => {},
  quitarDelLienzo: () => {},
  devolverAlLienzo: () => {},
  agregarDeCreaciones: () => {},
  abrirAgregar: () => {},
  avisarError: () => {},
});
export const useDiseno = (): ValorDeDiseno => useContext(DisenoContext);
